import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Input from 'components/elements/Form/Input/input';
import IntegrationKeysIcon from 'assets/Icons/sidebarIcons/IntegrationKeysIcon';
import { useQuery } from 'react-query';
import LoadingSpinner from 'components/elements/Spinner/LoadingSpinner';
import useSaveKey from './crud/useSave';
import { IntegrationKeyProps } from 'services/hooks/integration-key-service';
import IntegrationService, { IntegrationProps } from 'services/hooks/integration-service';
import SelectEntity from 'components/structure/Common/ListsEntity/SelectEntity';
import Modal from 'components/elements/Modal';
import PrimaryButton from 'components/elements/Form/button/primaryButton';
import { toast } from 'react-toastify';
import {
  generateApiFailErrorMessage,
  generateSaveErrorMessage,
  generateSaveSuccessMessage,
  generateUpdateErrorMessage,
  generateUpdateSuccessMessage
} from 'utils/helpers';
import SelectInput from 'components/elements/Form/select/selectInput';
import { AccessIntegrationName, LatchKeyTypes } from 'utils/constants';
import IntegrationPreferenceService, {
  IntegrationPreferenceProps
} from 'services/hooks/integration-preference-service';
import RoleSelectionForm from './roleSelectionForm';

type CreateProps = {
  entityName: string;
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  setEditUUID?: React.Dispatch<React.SetStateAction<string>>;
  createQuery: any;
  updateQuery: any;
  editQuery?: any;
  editUUID?: string;
  queryName: string;
};

// Validation
const CreateKeySchema = yup.object().shape({
  integrationUuid: yup.string().required('Integration is required'),
  accountUuid: yup.string().required('Account is required'),
  buildingUuid: yup.string().required('Building is required'),
  organizationUuid: yup.string().required('Organization is required')
});

export default function Save({
  entityName,
  showModal,
  setEditUUID,
  setShowModal,
  createQuery,
  updateQuery,
  editQuery,
  editUUID,
  queryName
}: CreateProps) {
  const [getKeyResult, setGetKeyResult] = useState<IntegrationKeyProps | null>(null);
  const [disabled, setDisabled] = useState(false);
  const [isLatchIntegration, setIsLatchIntegration] = useState(false);
  const [intPreferenceUuid, setIntPreferenceUuid] = useState<string>('');

  const { isSuccess, data, refetch } = useQuery<IntegrationKeyProps, Error>(
    ['key', editUUID],
    () => editQuery(editUUID),
    {
      enabled: editUUID ? true : false,
      onError: () => {
        toast.error(generateApiFailErrorMessage('Integration Key'));
      }
    }
  );

  const methods = useForm({
    defaultValues: getKeyResult
      ? {
          uuid: getKeyResult?.uuid,
          clientId: getKeyResult?.clientId,
          clientSecret: getKeyResult?.clientSecret,
          keyType: getKeyResult?.keyType,
          preferenceUuid: getKeyResult?.preferenceUuid,
          organizationUuid: getKeyResult?.organizationUuid
        }
      : {
          uuid: '',
          clientId: '',
          clientSecret: '',
          keyType: '',
          preferenceUuid: '',
          integrationUuid: '',
          latchKeyType: '',
          accountUuid: '',
          buildingUuid: '',
          organizationUuid: ''
        },
    resolver: yupResolver(CreateKeySchema)
  });

  // Fetch selected integration's data
  const { data: preferenceData } = useQuery<IntegrationPreferenceProps, Error>(
    ['integration', !!editUUID, data?.preferenceUuid],
    () => IntegrationPreferenceService.findIntegrationPreferenceById(data?.preferenceUuid),
    {
      enabled: !!editUUID && !!data?.preferenceUuid,
      onError: () => {
        toast.error(generateApiFailErrorMessage('Integration Preference'));
      }
    }
  );

  useEffect(() => {
    if (data && editUUID) {
      refetch();
      setGetKeyResult(data);
      methods.reset({
        ...data,
        integrationUuid: preferenceData ? preferenceData?.integrationUuid : ''
      });
    }
  }, [data, editUUID, methods, preferenceData, refetch]);

  // Separate effect for handling changes specific to `isLatchIntegration` when form is in Edit mode
  useEffect(() => {
    if (isLatchIntegration && editUUID) {
      // Update form values related to isLatchIntegration without resetting the entire form
      methods.setValue('latchKeyType', isLatchIntegration ? data?.keyType : '', {
        shouldValidate: true
      });
      methods.setValue('keyType', '', { shouldValidate: false });
    } else {
      methods.setValue('latchKeyType', '', { shouldValidate: false });
    }
  }, [isLatchIntegration, methods, data?.keyType, editUUID]);

  const saveKeyMutation = useSaveKey({
    createQuery,
    updateQuery,
    queryName,
    onSuccess: () => {
      setShowModal(false);
      setDisabled(false);
      if (!editUUID) {
        toast.success(generateSaveSuccessMessage('Integration Key'));
      } else {
        toast.success(generateUpdateSuccessMessage('Integration Key'));
      }
      if (setEditUUID) setEditUUID('');
    },
    onError: () => {
      setDisabled(false);
      if (!editUUID) {
        toast.error(generateSaveErrorMessage('Integration Key'));
      } else {
        toast.error(generateUpdateErrorMessage('Integration Key'));
      }
    }
  });

  /* The below line of code is using the `watch` method to watch for changes in the input field with the
  name 'integrationUuid'. */
  const integrationUuidWatch = methods.watch('integrationUuid');

  // Fetch selected integration's data
  const { data: integrationData } = useQuery<IntegrationProps, Error>(
    ['integration', integrationUuidWatch],
    () => IntegrationService.findIntegrationById(integrationUuidWatch),
    {
      enabled: !!integrationUuidWatch,
      onError: () => {
        toast.error(generateApiFailErrorMessage('Integration'));
      }
    }
  );

  useEffect(() => {
    // Set selected Integration's Integration Preference UUID
    if (integrationData && integrationData.preferences.uuid) {
      setIntPreferenceUuid(integrationData.preferences.uuid);
    } else {
      setIntPreferenceUuid('');
    }

    if (integrationData && integrationData?.name === AccessIntegrationName.Latch) {
      setIsLatchIntegration(true);
    } else {
      setIsLatchIntegration(false);
    }
  }, [integrationData, integrationUuidWatch]);

  const onSubmit = async (e: IntegrationKeyProps) => {
    setDisabled(true);
    let selectedKeyType: string | undefined;

    if (isLatchIntegration) {
      selectedKeyType = e?.latchKeyType;
    } else {
      selectedKeyType = e?.keyType;
    }

    if (intPreferenceUuid) {
      saveKeyMutation.mutate({
        uuid: e?.uuid,
        clientId: e?.clientId,
        clientSecret: e?.clientSecret,
        keyType: selectedKeyType,
        preferenceUuid: intPreferenceUuid,
        organizationUuid: e?.organizationUuid
      });
    } else {
      setDisabled(false);
      toast.error(
        'Integration Preference Id not found for the selected Integration. Please try again or contact admin if the issue persists.'
      );
    }
  };

  return !editUUID || isSuccess ? (
    <>
      <Modal
        isOpen={showModal}
        onRequestClose={() => {
          setShowModal(false);
          if (setEditUUID) setEditUUID('');
        }}
        title={entityName}
      >
        <form onSubmit={methods.handleSubmit(onSubmit)} className={`text-left items-start`}>
          <div className={'grid grid-cols-1 sm:grid-cols-2 gap-4 mr-8'}>
            {preferenceData || !editUUID ? (
              <SelectEntity
                Controller={Controller}
                control={methods.control}
                label="Choose an Integration"
                placeholder="Select an Integration"
                queryname="integrations"
                name="integrationUuid"
                errors={methods.formState.errors}
                query={() => IntegrationService.findAllIntegrations()}
                expectedLabel="Integration/Name"
                multi={false}
              />
            ) : (
              <LoadingSpinner /> // Show loading indicator while default values are loading on Edit mode
            )}
            <Input
              label="Client Id"
              name="clientId"
              type="password"
              placeholder="Enter Client Id"
              autoFocus
              methods={methods}
              autoComplete="new-password"
            />
            <Input
              label="Client Secret"
              name="clientSecret"
              type="password"
              placeholder="Enter Client Secret"
              autoFocus
              methods={methods}
              autoComplete="new-password"
            />
            {isLatchIntegration ? (
              <>
                <SelectInput
                  Controller={Controller}
                  control={methods.control}
                  label="key Type"
                  name="latchKeyType"
                  errors={methods.formState.errors}
                  placeholder="Select a Key Type"
                  options={LatchKeyTypes}
                />
              </>
            ) : (
              <Input
                label="key Type"
                name="keyType"
                type="text"
                placeholder="Enter Key Type"
                autoFocus
                methods={methods}
              />
            )}
            <RoleSelectionForm Controller={Controller} methods={methods} editUUID={editUUID} />
          </div>
          <PrimaryButton
            buttonLabel={entityName}
            iconLeft={<IntegrationKeysIcon />}
            variant="default"
            size={'mt-4 px-6 w-auto gap-2'}
            disabled={disabled}
          />
        </form>
      </Modal>
    </>
  ) : (
    <LoadingSpinner />
  );
}
